import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteSettingsApi, UpdateSiteSettingRequest } from '../services/api';
import toast from 'react-hot-toast';
import AdminEmailTemplates from './AdminEmailTemplates';
import ConfirmationModal from './ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';

type SectionType = 'general' | 'urls' | 'seo' | 'contact' | 'social' | 'system' | 'analytics' | 'legal' | 'features' | 'email' | 'email-templates';

const AdminSiteSettings: React.FC = () => {
  const [activeSection, setActiveSection] = useState<SectionType>('general');
  const [formData, setFormData] = useState<UpdateSiteSettingRequest>({
    frontendUrl: '',
    backendUrl: '',
    siteTitle: '',
    siteDescription: '',
    businessName: '',
    contactEmail: '',
    siteAuthor: '',
    defaultLanguage: 'fa',
    copyrightText: '',
    maintenanceMode: false,
    siteActive: true,
    maxFileUploadSizeMB: 10,
    allowedFileTypes: 'jpg,jpeg,png,gif,pdf,doc,docx',
    defaultCurrency: 'IRR',
    timezone: 'Asia/Tehran',
    enableRegistration: true,
    enableGuestPosting: false,
    enableMessaging: true,
    enableReviews: false,
    enableOtpOnlyLogin: true,
  });

  const queryClient = useQueryClient();

  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  // Fetch current settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: async () => {
      const response = await siteSettingsApi.get();
      return response.data;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSiteSettingRequest) => siteSettingsApi.update(data),
    onSuccess: () => {
      toast.success('تنظیمات با موفقیت به‌روزرسانی شد!', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl'
        }
      });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      console.error('Error updating settings:', error);
      toast.error('خطا در به‌روزرسانی تنظیمات', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl'
        }
      });
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: () => siteSettingsApi.initialize(),
    onSuccess: () => {
      toast.success('تنظیمات پیش‌فرض با موفقیت ایجاد شد!', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl'
        }
      });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      console.error('Error initializing settings:', error);
      toast.error('خطا در ایجاد تنظیمات پیش‌فرض', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl'
        }
      });
    },
  });

  // Update form data when settings are loaded
  useEffect(() => {
    if (settings) {
      setFormData({
        frontendUrl: settings.frontendUrl,
        backendUrl: settings.backendUrl,
        apiBaseUrl: settings.apiBaseUrl,
        cdnUrl: settings.cdnUrl,
        siteTitle: settings.siteTitle,
        siteDescription: settings.siteDescription,
        metaKeywords: settings.metaKeywords,
        siteLogoUrl: settings.siteLogoUrl,
        faviconUrl: settings.faviconUrl,
        openGraphImageUrl: settings.openGraphImageUrl,
        siteAuthor: settings.siteAuthor,
        defaultLanguage: settings.defaultLanguage,
        businessName: settings.businessName,
        contactEmail: settings.contactEmail,
        supportEmail: settings.supportEmail,
        contactPhone: settings.contactPhone,
        businessAddress: settings.businessAddress,
        copyrightText: settings.copyrightText,
        facebookUrl: settings.facebookUrl,
        twitterUrl: settings.twitterUrl,
        instagramUrl: settings.instagramUrl,
        telegramUrl: settings.telegramUrl,
        linkedInUrl: settings.linkedInUrl,
        maintenanceMode: settings.maintenanceMode,
        siteActive: settings.siteActive,
        maxFileUploadSizeMB: settings.maxFileUploadSizeMB,
        allowedFileTypes: settings.allowedFileTypes,
        defaultCurrency: settings.defaultCurrency,
        timezone: settings.timezone,
        googleAnalyticsId: settings.googleAnalyticsId,
        googleTagManagerId: settings.googleTagManagerId,
        facebookPixelId: settings.facebookPixelId,
        termsOfServiceUrl: settings.termsOfServiceUrl,
        privacyPolicyUrl: settings.privacyPolicyUrl,
        cookiePolicyUrl: settings.cookiePolicyUrl,
        enableRegistration: settings.enableRegistration,
        enableGuestPosting: settings.enableGuestPosting,
        enableMessaging: settings.enableMessaging,
        enableReviews: settings.enableReviews,
        enableOtpOnlyLogin: settings.enableOtpOnlyLogin,
      });
    }
  }, [settings]);

  const handleInputChange = (field: keyof UpdateSiteSettingRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleInitialize = () => {
    showModal({
      title: 'ایجاد تنظیمات پیش‌فرض',
      message: 'آیا می‌خواهید تنظیمات پیش‌فرض را ایجاد کنید؟',
      confirmText: 'بله',
      cancelText: 'خیر',
      type: 'info',
      isRtl: true,
      onConfirm: () => {
        initializeMutation.mutate();
      }
    });
  };

  const sections = [
    { id: 'general', name: 'عمومی', icon: '🏠' },
    { id: 'urls', name: 'آدرس‌ها', icon: '🔗' },
    { id: 'seo', name: 'سئو و متادیتا', icon: '🔍' },
    { id: 'contact', name: 'اطلاعات تماس', icon: '📞' },
    { id: 'social', name: 'شبکه‌های اجتماعی', icon: '📱' },
    { id: 'system', name: 'سیستم', icon: '⚙️' },
    { id: 'analytics', name: 'تحلیل‌ها', icon: '📊' },
    { id: 'legal', name: 'قانونی', icon: '📋' },
    { id: 'features', name: 'ویژگی‌ها', icon: '🎯' },
    { id: 'email', name: 'ایمیل', icon: '✉️' },
    { id: 'email-templates', name: 'قالب‌های ایمیل', icon: '📧' },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center" dir="rtl">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">تنظیمات یافت نشد</h3>
        <p className="text-gray-500 mb-6">
          می‌توانید تنظیمات پیش‌فرض را برای اولین بار ایجاد کنید.
        </p>
        <button
          onClick={handleInitialize}
          disabled={initializeMutation.isPending}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {initializeMutation.isPending ? 'در حال ایجاد...' : 'ایجاد تنظیمات پیش‌فرض'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">تنظیمات سایت</h2>
            <p className="text-blue-100 text-sm">مدیریت تنظیمات سراسری سیستم</p>
          </div>
          <div className="text-3xl text-white">⚙️</div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200">
          <div className="p-4">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as SectionType)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="ml-3 text-lg">{section.icon}</span>
                  {section.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {activeSection === 'email-templates' ? (
            <AdminEmailTemplates />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* General Settings */}
            {activeSection === 'general' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات عمومی</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      عنوان سایت
                    </label>
                    <input
                      type="text"
                      value={formData.siteTitle}
                      onChange={(e) => handleInputChange('siteTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      نام کسب‌وکار
                    </label>
                    <input
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    توضیحات سایت
                  </label>
                  <textarea
                    value={formData.siteDescription}
                    onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      نویسنده سایت
                    </label>
                    <input
                      type="text"
                      value={formData.siteAuthor}
                      onChange={(e) => handleInputChange('siteAuthor', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      زبان پیش‌فرض
                    </label>
                    <select
                      value={formData.defaultLanguage}
                      onChange={(e) => handleInputChange('defaultLanguage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="fa">فارسی</option>
                      <option value="en">انگلیسی</option>
                      <option value="ar">عربی</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    متن کپی‌رایت
                  </label>
                  <input
                    type="text"
                    value={formData.copyrightText}
                    onChange={(e) => handleInputChange('copyrightText', e.target.value)}
                    placeholder="© 2024 نام شرکت. تمامی حقوق محفوظ است."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* URLs Settings */}
            {activeSection === 'urls' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات آدرس‌ها</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس فرانت‌اند
                    </label>
                    <input
                      type="url"
                      value={formData.frontendUrl}
                      onChange={(e) => handleInputChange('frontendUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس بک‌اند
                    </label>
                    <input
                      type="url"
                      value={formData.backendUrl}
                      onChange={(e) => handleInputChange('backendUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس API (اختیاری)
                    </label>
                    <input
                      type="url"
                      value={formData.apiBaseUrl || ''}
                      onChange={(e) => handleInputChange('apiBaseUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس CDN (اختیاری)
                    </label>
                    <input
                      type="url"
                      value={formData.cdnUrl || ''}
                      onChange={(e) => handleInputChange('cdnUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SEO & Metadata Settings */}
            {activeSection === 'seo' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات سئو و متادیتا</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    کلمات کلیدی متا
                  </label>
                  <textarea
                    value={formData.metaKeywords || ''}
                    onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
                    rows={2}
                    placeholder="کلمات کلیدی را با کاما جدا کنید"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس لوگو
                    </label>
                    <input
                      type="url"
                      value={formData.siteLogoUrl || ''}
                      onChange={(e) => handleInputChange('siteLogoUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      آدرس فاوآیکون
                    </label>
                    <input
                      type="url"
                      value={formData.faviconUrl || ''}
                      onChange={(e) => handleInputChange('faviconUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تصویر Open Graph
                  </label>
                  <input
                    type="url"
                    value={formData.openGraphImageUrl || ''}
                    onChange={(e) => handleInputChange('openGraphImageUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Contact Settings */}
            {activeSection === 'contact' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">اطلاعات تماس</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ایمیل تماس
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ایمیل پشتیبانی
                    </label>
                    <input
                      type="email"
                      value={formData.supportEmail || ''}
                      onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تلفن تماس
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone || ''}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس کسب‌وکار
                  </label>
                  <textarea
                    value={formData.businessAddress || ''}
                    onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Social Media Settings */}
            {activeSection === 'social' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">شبکه‌های اجتماعی</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      فیسبوک
                    </label>
                    <input
                      type="url"
                      value={formData.facebookUrl || ''}
                      onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                      placeholder="https://facebook.com/yourpage"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      توییتر
                    </label>
                    <input
                      type="url"
                      value={formData.twitterUrl || ''}
                      onChange={(e) => handleInputChange('twitterUrl', e.target.value)}
                      placeholder="https://twitter.com/yourhandle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اینستاگرام
                    </label>
                    <input
                      type="url"
                      value={formData.instagramUrl || ''}
                      onChange={(e) => handleInputChange('instagramUrl', e.target.value)}
                      placeholder="https://instagram.com/yourhandle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      تلگرام
                    </label>
                    <input
                      type="url"
                      value={formData.telegramUrl || ''}
                      onChange={(e) => handleInputChange('telegramUrl', e.target.value)}
                      placeholder="https://t.me/yourchannel"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    لینکدین
                  </label>
                  <input
                    type="url"
                    value={formData.linkedInUrl || ''}
                    onChange={(e) => handleInputChange('linkedInUrl', e.target.value)}
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* System Settings */}
            {activeSection === 'system' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات سیستم</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      حداکثر اندازه فایل (مگابایت)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.maxFileUploadSizeMB}
                      onChange={(e) => handleInputChange('maxFileUploadSizeMB', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ارز پیش‌فرض
                    </label>
                    <select
                      value={formData.defaultCurrency}
                      onChange={(e) => handleInputChange('defaultCurrency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="IRR">ریال ایران</option>
                      <option value="USD">دلار آمریکا</option>
                      <option value="EUR">یورو</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    انواع فایل‌های مجاز
                  </label>
                  <input
                    type="text"
                    value={formData.allowedFileTypes}
                    onChange={(e) => handleInputChange('allowedFileTypes', e.target.value)}
                    placeholder="jpg,jpeg,png,gif,pdf,doc,docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">فرمت‌ها را با کاما جدا کنید</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    منطقه زمانی
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Asia/Tehran">تهران</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">لندن</option>
                    <option value="America/New_York">نیویورک</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      checked={formData.maintenanceMode}
                      onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="maintenanceMode" className="text-sm font-medium text-gray-700">
                      حالت تعمیرات
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="siteActive"
                      checked={formData.siteActive}
                      onChange={(e) => handleInputChange('siteActive', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="siteActive" className="text-sm font-medium text-gray-700">
                      سایت فعال
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Settings */}
            {activeSection === 'analytics' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات تحلیل‌ها</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    شناسه Google Analytics
                  </label>
                  <input
                    type="text"
                    value={formData.googleAnalyticsId || ''}
                    onChange={(e) => handleInputChange('googleAnalyticsId', e.target.value)}
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    شناسه Google Tag Manager
                  </label>
                  <input
                    type="text"
                    value={formData.googleTagManagerId || ''}
                    onChange={(e) => handleInputChange('googleTagManagerId', e.target.value)}
                    placeholder="GTM-XXXXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    شناسه Facebook Pixel
                  </label>
                  <input
                    type="text"
                    value={formData.facebookPixelId || ''}
                    onChange={(e) => handleInputChange('facebookPixelId', e.target.value)}
                    placeholder="xxxxxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Legal Settings */}
            {activeSection === 'legal' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">صفحات قانونی</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس شرایط استفاده
                  </label>
                  <input
                    type="url"
                    value={formData.termsOfServiceUrl || ''}
                    onChange={(e) => handleInputChange('termsOfServiceUrl', e.target.value)}
                    placeholder="https://yoursite.com/terms"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس حریم خصوصی
                  </label>
                  <input
                    type="url"
                    value={formData.privacyPolicyUrl || ''}
                    onChange={(e) => handleInputChange('privacyPolicyUrl', e.target.value)}
                    placeholder="https://yoursite.com/privacy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    آدرس سیاست کوکی
                  </label>
                  <input
                    type="url"
                    value={formData.cookiePolicyUrl || ''}
                    onChange={(e) => handleInputChange('cookiePolicyUrl', e.target.value)}
                    placeholder="https://yoursite.com/cookies"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Features Settings */}
            {activeSection === 'features' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">ویژگی‌های سایت</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableRegistration"
                      checked={formData.enableRegistration}
                      onChange={(e) => handleInputChange('enableRegistration', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableRegistration" className="text-sm font-medium text-gray-700">
                      فعال‌سازی ثبت‌نام
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableGuestPosting"
                      checked={formData.enableGuestPosting}
                      onChange={(e) => handleInputChange('enableGuestPosting', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableGuestPosting" className="text-sm font-medium text-gray-700">
                      ارسال آگهی بدون ثبت‌نام
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableMessaging"
                      checked={formData.enableMessaging}
                      onChange={(e) => handleInputChange('enableMessaging', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableMessaging" className="text-sm font-medium text-gray-700">
                      سیستم پیام‌رسانی
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableReviews"
                      checked={formData.enableReviews}
                      onChange={(e) => handleInputChange('enableReviews', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableReviews" className="text-sm font-medium text-gray-700">
                      سیستم نظرات و امتیازدهی
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableOtpOnlyLogin"
                      checked={formData.enableOtpOnlyLogin}
                      onChange={(e) => handleInputChange('enableOtpOnlyLogin', e.target.checked)}
                      className="ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableOtpOnlyLogin" className="text-sm font-medium text-gray-700">
                      ورود فقط با OTP (غیرفعال کردن ورود با ایمیل/رمز عبور)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings */}
            {activeSection === 'email' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">تنظیمات ایمیل</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="mr-3">
                      <p className="text-sm text-blue-700">
                        <strong>پیکربندی SMTP:</strong> تنظیمات ایمیل اکنون در فایل‌های پیکربندی سرور برای امنیت بیشتر تنظیم شده است. برای تغییر تنظیمات SMTP با مدیر سیستم تماس بگیرید.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex justify-end space-x-reverse space-x-3">
                <button
                  type="button"
                  onClick={() => setActiveSection('general')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  لغو
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'OK'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'info'}
      />
    </div>
  );
};

export default AdminSiteSettings; 